import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

const UpdateAPIClientMutation = graphql(/* GraphQL */ `
  mutation updateAPIClient($input: UpdateAPIClientInput!) {
    updateAPIClient(input: $input) {
      ... on UpdateAPIClientSuccess {
        client {
          id
          clientId
          clientName
          createdAt
          redirectUris
          privacyPolicyUri
          logoUri
          isSuspended
          scopes {
            name
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on InvalidInputError {
        message
      }
      ... on InvalidRedirectUrisError {
        message
      }
    }
  }
`)

export type UpdateAPIClientInput = {
  clientId: string
  clientName: string
  redirectUris: string[]
  privacyPolicyUri?: string
  logoUri?: string | null
  scopes?: string[]
}

type UpdateAPIClientResult =
  | { success: true }
  | { success: false; error: string }

type UseUpdateAPIClientResult = {
  updateClient: (input: UpdateAPIClientInput) => Promise<UpdateAPIClientResult>
  isLoading: boolean
}

export const useUpdateAPIClient = (): UseUpdateAPIClientResult => {
  const [mutate, { loading }] = useMutation(UpdateAPIClientMutation)

  const updateClient = async (
    input: UpdateAPIClientInput,
  ): Promise<UpdateAPIClientResult> => {
    try {
      const result = await mutate({
        variables: { input },
      })

      if (result.error) {
        return { success: false, error: result.error.message }
      }

      const response = result.data?.updateAPIClient

      if (response && 'client' in response) {
        return { success: true }
      }

      if (response && 'message' in response) {
        return { success: false, error: response.message }
      }

      return {
        success: false,
        error: 'Failed to update client. Please try again.',
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'

      return { success: false, error: message }
    }
  }

  return {
    updateClient,
    isLoading: loading,
  }
}
